import React, { useState, useEffect } from 'react';
import '../../../styles/FloralWedding.css';

const FloralWedding = ({ coupleNames }) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [showRsvp, setShowRsvp] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [rsvpData, setRsvpData] = useState({
    name: '',
    attending: '',
    guests: '1',
    message: ''
  });

  // Parse couple names
  const formatCoupleNames = (names) => {
    if (!names) return 'Bride & Groom';
    return names
      .split('-')
      .map(name => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
      .join(' & ');
  };

  const invitationData = {
    coupleNames: formatCoupleNames(coupleNames),
    groomName: coupleNames?.split('-')[0] || 'John',
    brideName: coupleNames?.split('-')[1] || 'Jane',
    weddingDate: 'December 14, 2024',
    weddingDay: 'Saturday',
    ceremonyTime: '3:30 PM',
    churchName: 'San Agustin Church',
    churchAddress: 'Intramuros, Manila',
    receptionVenue: 'Manila Hotel',
    receptionAddress: 'One Rizal Park, Manila',
    message: 'Together with their families, request the honor of your presence at the celebration of their marriage.',
    dressCode: 'Formal Attire',
    colors: 'Blush Pink & Sage Green'
  };

  useEffect(() => {
    // Auto-reveal after a short delay
    const timer = setTimeout(() => setIsRevealed(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleRsvpChange = (e) => {
    const { name, value } = e.target;
    setRsvpData(prev => ({ ...prev, [name]: value }));
  };

  const handleRsvpSubmit = (e) => {
    e.preventDefault();
    console.log('RSVP Submitted:', rsvpData);
    setRsvpSubmitted(true);
  };

  return (
    <div className="floral-invitation">
      {/* Floating Petals Animation */}
      <div className="floating-petals">
        {[...Array(15)].map((_, i) => (
          <div key={i} className={`petal petal-${i + 1}`}>🌸</div>
        ))}
      </div>

      {/* Main Content */}
      <div className={`floral-container ${isRevealed ? 'revealed' : ''}`}>
        {/* Navigation */}
        <nav className="floral-nav">
          <button 
            className={activeSection === 'home' ? 'active' : ''} 
            onClick={() => setActiveSection('home')}
          >
            Home
          </button>
          <button 
            className={activeSection === 'details' ? 'active' : ''} 
            onClick={() => setActiveSection('details')}
          >
            Details
          </button>
          <button 
            className={activeSection === 'rsvp' ? 'active' : ''} 
            onClick={() => setActiveSection('rsvp')}
          >
            RSVP
          </button>
        </nav>

        {/* Home Section */}
        {activeSection === 'home' && (
          <section className="floral-section home-section">
            <div className="floral-frame">
              <div className="corner-flower top-left">🌿</div>
              <div className="corner-flower top-right">🌿</div>
              <div className="corner-flower bottom-left">🌸</div>
              <div className="corner-flower bottom-right">🌸</div>
              
              <div className="home-content">
                <p className="invitation-prefix">Together with their families</p>
                
                <div className="couple-names">
                  <span className="groom-name">{invitationData.groomName}</span>
                  <span className="and-symbol">&</span>
                  <span className="bride-name">{invitationData.brideName}</span>
                </div>

                <div className="divider">
                  <span>✦</span>
                  <div className="line"></div>
                  <span>💕</span>
                  <div className="line"></div>
                  <span>✦</span>
                </div>

                <p className="invitation-text">{invitationData.message}</p>

                <div className="wedding-date-display">
                  <div className="date-item">
                    <span className="date-label">Saturday</span>
                  </div>
                  <div className="date-item main">
                    <span className="date-day">14</span>
                    <span className="date-month">December</span>
                    <span className="date-year">2024</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">{invitationData.ceremonyTime}</span>
                  </div>
                </div>

                <button 
                  className="view-details-btn"
                  onClick={() => setActiveSection('details')}
                >
                  View Details
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Details Section */}
        {activeSection === 'details' && (
          <section className="floral-section details-section">
            <h2 className="section-title">
              <span>🌸</span> Wedding Details <span>🌸</span>
            </h2>

            <div className="details-grid">
              <div className="detail-card">
                <div className="detail-icon">⛪</div>
                <h3>Ceremony</h3>
                <p className="venue-name">{invitationData.churchName}</p>
                <p className="venue-address">{invitationData.churchAddress}</p>
                <p className="venue-time">{invitationData.ceremonyTime}</p>
              </div>

              <div className="detail-card">
                <div className="detail-icon">🎉</div>
                <h3>Reception</h3>
                <p className="venue-name">{invitationData.receptionVenue}</p>
                <p className="venue-address">{invitationData.receptionAddress}</p>
                <p className="venue-time">Immediately following the ceremony</p>
              </div>

              <div className="detail-card">
                <div className="detail-icon">👗</div>
                <h3>Dress Code</h3>
                <p className="dress-info">{invitationData.dressCode}</p>
                <p className="color-info">Colors: {invitationData.colors}</p>
              </div>

              <div className="detail-card">
                <div className="detail-icon">🎁</div>
                <h3>Gift Registry</h3>
                <p className="gift-message">Your presence is the greatest gift.</p>
                <p className="gift-note">However, if you wish to bless us, monetary gifts are appreciated.</p>
              </div>
            </div>

            <div className="countdown-section">
              <h3>Counting Down to Forever</h3>
              <div className="countdown-display">
                <div className="countdown-item">
                  <span className="countdown-number">--</span>
                  <span className="countdown-label">Days</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">--</span>
                  <span className="countdown-label">Hours</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">--</span>
                  <span className="countdown-label">Minutes</span>
                </div>
                <div className="countdown-item">
                  <span className="countdown-number">--</span>
                  <span className="countdown-label">Seconds</span>
                </div>
              </div>
            </div>

            <button 
              className="rsvp-cta-btn"
              onClick={() => setActiveSection('rsvp')}
            >
              RSVP Now
            </button>
          </section>
        )}

        {/* RSVP Section */}
        {activeSection === 'rsvp' && (
          <section className="floral-section rsvp-section">
            <h2 className="section-title">
              <span>💌</span> RSVP <span>💌</span>
            </h2>

            {!rsvpSubmitted ? (
              <form className="floral-rsvp-form" onSubmit={handleRsvpSubmit}>
                <p className="rsvp-intro">We would be honored to have you celebrate with us!</p>
                <p className="rsvp-deadline">Kindly respond by November 30, 2024</p>

                <div className="form-group">
                  <label>Your Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={rsvpData.name}
                    onChange={handleRsvpChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Will you be attending?</label>
                  <div className="attendance-options">
                    <label className={`attendance-option ${rsvpData.attending === 'yes' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="attending"
                        value="yes"
                        checked={rsvpData.attending === 'yes'}
                        onChange={handleRsvpChange}
                        required
                      />
                      <span className="option-icon">🎉</span>
                      <span className="option-text">Joyfully Accepts</span>
                    </label>
                    <label className={`attendance-option ${rsvpData.attending === 'no' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="attending"
                        value="no"
                        checked={rsvpData.attending === 'no'}
                        onChange={handleRsvpChange}
                      />
                      <span className="option-icon">😢</span>
                      <span className="option-text">Regretfully Declines</span>
                    </label>
                  </div>
                </div>

                {rsvpData.attending === 'yes' && (
                  <div className="form-group">
                    <label>Number of Guests</label>
                    <select
                      name="guests"
                      value={rsvpData.guests}
                      onChange={handleRsvpChange}
                    >
                      <option value="1">1 Guest</option>
                      <option value="2">2 Guests</option>
                      <option value="3">3 Guests</option>
                      <option value="4">4 Guests</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Message for the Couple (Optional)</label>
                  <textarea
                    name="message"
                    value={rsvpData.message}
                    onChange={handleRsvpChange}
                    placeholder="Share your wishes..."
                    rows="4"
                  />
                </div>

                <button type="submit" className="submit-rsvp-btn">
                  Send RSVP
                </button>
              </form>
            ) : (
              <div className="rsvp-confirmation">
                <div className="confirmation-icon">💐</div>
                <h3>Thank You!</h3>
                <p>Your response has been recorded.</p>
                <p className="confirmed-name">{rsvpData.name}</p>
                {rsvpData.attending === 'yes' ? (
                  <p className="confirmation-message">We can't wait to celebrate with you! 🎊</p>
                ) : (
                  <p className="confirmation-message">We'll miss you! Thank you for letting us know. 💕</p>
                )}
              </div>
            )}
          </section>
        )}

        {/* Watermark */}
        <div className="floral-watermark">
          <p>Created with 💕 by <a href="https://logicatechnology.com" target="_blank" rel="noopener noreferrer">Logica Technology</a></p>
        </div>
      </div>
    </div>
  );
};

export default FloralWedding;
